import { bidAgeLabel, bidHistoryLabel, type AuctionBidHistoryEntry } from '@auctisync/api-client';
import { formatCents } from '@auctisync/shared';
import type { ReactNode } from 'react';

/**
 * "Your standing" and "Activity" — the two panels that ride in a sale header.
 *
 * 🔴 EXTRACTED SO FOUR PAGES CANNOT DRIFT. Jim, 2026-09-18: *"I want both of
 * those tables in the headers on Home, Lot, and Watchlist on the website."*
 * They lived inline in the Live Room, which is fine while one page renders them
 * and a guarantee of divergence the moment four do — a different empty string
 * here, a different guest message there, and the same bidder gets told two
 * things about the same sale depending on which page they opened.
 *
 * PRESENTATIONAL ONLY. Each page already knows, or can fetch, its own standing
 * and its own feed; this decides how they LOOK, not where they come from. The
 * Live Room has both in hand already and passes them straight through, so
 * putting these panels on three more pages costs it nothing.
 */

export interface SaleStandingCounts {
  winning: number;
  outbid: number;
  watching: number;
  /** How many sales those lots span. Only meaningful, and only shown, when the
   *  panel describes more than one — see `scope`. */
  sales?: number;
}

export interface SaleAsideProps {
  /** Null for a signed-out reader — the panel then explains rather than showing zeroes. */
  standing: SaleStandingCounts | null;
  /**
   * What the panels are describing. `'sale'` is one auction — Home, the lot
   * page, the Live Room. `'everywhere'` is every sale the bidder is involved
   * in, which is the WATCHLIST and nothing else: a watchlist can hold lots from
   * three auctions, so "your standing across this sale" has no answer there.
   *
   * 🔴 THIS CHANGES THE WORDS, NOT JUST THE NUMBERS. A panel that said "across
   * this sale" over a figure spanning three of them would be a smaller lie than
   * showing the wrong figure, but a lie either way.
   */
  scope?: 'sale' | 'everywhere';
  events: readonly AuctionBidHistoryEntry[];
  /** The feed STOPS when the connection drops; it never shows stale bids as live. */
  offline: boolean;
  nowServerMs: () => number;
  /**
   * Sits between the two panels. The Live Room puts its exposure meter here —
   * that needs the lots it is DISPLAYING, which only that page has, so it stays
   * the caller's business rather than becoming a prop this component fakes.
   */
  children?: ReactNode;
}

export function SaleAside({
  standing,
  events,
  offline,
  nowServerMs,
  scope = 'sale',
  children,
}: SaleAsideProps) {
  const everywhere = scope === 'everywhere';
  return (
    /* 🔴 THE PANELS ARE DIRECT CHILDREN. They were once wrapped in a flex
       column, which made the aside grid see ONE item: `auto-fit` produced a
       660px track and collapsed the second to zero, so the two stacked at full
       width instead of sitting side by side at the ~324px they were drawn for. */
    <div className="lr-top-aside">
      <div className="wl-panel">
        <div className="wl-ph">
          <span className="w-lbl">Your standing</span>
        </div>
        <div style={{ padding: '4px 14px' }}>
          {standing === null ? (
            <p
              className="muted"
              style={{ fontSize: 13, padding: '10px 0', lineHeight: 1.5, margin: 0 }}
            >
              {everywhere
                ? 'Sign in to see where you stand across every sale you are in.'
                : 'Sign in to see where you stand across this sale.'}
            </p>
          ) : (
            <>
              <div className="w-kv" style={{ fontSize: 13.5 }}>
                <span className="tag t-win">Winning</span>
                <b className="mono">{standing.winning}</b>
              </div>
              <div className="w-kv" style={{ fontSize: 13.5 }}>
                <span className="tag t-out">Outbid</span>
                <b className="mono">{standing.outbid}</b>
              </div>
              <div className="w-kv" style={{ fontSize: 13.5 }}>
                <span className="muted">Watching</span>
                <b className="mono">{standing.watching}</b>
              </div>
              {/* Only on the watchlist, and only once there is more than one
                  sale to span — otherwise it is a line that says "1 sale" to a
                  bidder who is looking at one sale. */}
              {everywhere && (standing.sales ?? 0) > 1 ? (
                <p
                  className="muted"
                  style={{ fontSize: 12, margin: '8px 0 2px', lineHeight: 1.45 }}
                >
                  Across {standing.sales} sales.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>

      {children}

      <div className="wl-panel">
        <div className="wl-ph">
          <span className="w-lbl">Activity</span>
          {offline ? (
            <span className="muted" style={{ fontSize: 11 }}>
              Paused
            </span>
          ) : (
            <span className="w-dot" style={{ background: 'var(--semantic-live)' }} aria-hidden />
          )}
        </div>
        {offline ? (
          <p
            className="muted"
            style={{ fontSize: 12.5, padding: '16px 14px', margin: 0, lineHeight: 1.5 }}
          >
            The feed stops rather than showing you old bids as though they were current. It resumes
            on reconnect.
          </p>
        ) : events.length === 0 ? (
          <p
            className="muted"
            style={{ fontSize: 12.5, padding: '16px 14px', margin: 0, lineHeight: 1.5 }}
          >
            {everywhere
              ? 'No bids yet on the lots you are following. They appear here as they land.'
              : 'No bids yet in this sale. They appear here as they land.'}
          </p>
        ) : (
          <div className="wl-tick">
            {events.map((e) => {
              const you = bidHistoryLabel(e) === 'you';
              return (
                <div className="wl-ev" key={e.id}>
                  <span
                    style={{
                      fontWeight: you ? 700 : 500,
                      color: you ? 'var(--text)' : 'var(--text-muted)',
                    }}
                  >
                    {you ? 'You' : 'bidder'}
                  </span>
                  <span className="muted">Lot {e.lotNumber}</span>
                  {/* NULL = the house hides winning info and this bid is not the
                      caller's. Never null for their own. */}
                  {e.amountCents === null ? (
                    <b className="muted">—</b>
                  ) : (
                    <b>{formatCents(e.amountCents)}</b>
                  )}
                  <span className="mono">{bidAgeLabel(e.createdAt, nowServerMs())}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
